import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Paper,
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Switch,
  FormControlLabel,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";
import httpclient from "../../../../Utils";

const formFields = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  profilePic: null,
  status: true,
  employeeCode: "",
  mobile: "",
};

const validateForm = (formData, mode) => {
  const errors = {};

  if (!formData.firstName?.trim()) {
    errors.firstName = "First name is required";
  }

  if (!formData.lastName?.trim()) {
    errors.lastName = "Last name is required";
  }

  if (!formData.email?.trim()) {
    errors.email = "Email is required";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = "Invalid email format";
  }

  // Employee code validation - must be 2 characters or less
  if (formData.employeeCode && formData.employeeCode.length > 2) {
    errors.employeeCode = "Employee code must be 2 characters or less";
  }

  // Mobile number validation - must be exactly 10 digits
  if (formData.mobile) {
    if (!/^\d{10}$/.test(formData.mobile)) {
      errors.mobile = "Mobile number must be exactly 10 digits";
    }
  }

  // Password validation - required for add mode, optional for edit
  if (mode === "add") {
    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
  } else if (mode === "edit" && formData.password) {
    // In edit mode, validate password only if it's provided
    if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
  }

  return errors;
};

function AddDriver() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const mode = id ? "edit" : "add";
  const [loading, setLoading] = useState(mode === "edit");

  const [formData, setFormData] = useState(formFields);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  // Fetch driver data if in edit mode
  useEffect(() => {
    const fetchDriverData = async () => {
      if (mode === "edit" && id) {
        setLoading(true);

        const API_URL = `delivery/drivers/details/${id}`;

        try {
          const res = await httpclient.get(API_URL);
          const { data } = res;

          if (data.status === 200) {
            // const driverData = data.data;

            const driverData = Array.isArray(data.data) ? data.data[0] : data.data;

            const modifiedData = {
              firstName: driverData.first_name || "",
              lastName: driverData.last_name || "",
              email: driverData.email || "",
              employeeCode: driverData.employee_code || "",
              mobile: driverData.mobile || "",
              profilePic: driverData.profile_photo || null,
              password: "",
              confirmPassword: "",
              status: driverData.status === 1,
            };
            setFormData(modifiedData);

            // Set profile photo preview if exists
            if (driverData.profile_photo) {
              setProfilePicPreview(driverData.profile_photo);
            }
          } else {
            showNotification(
              "error",
              data.message || "Failed to fetch driver data"
            );
          }
        } catch (error) {
          if (error.response?.status === 401) {
            refresh();
          } else {
            handleApiError(error);
          }
        } finally {
          setLoading(false);
        }
      }
    };

    fetchDriverData();
  }, [mode, id]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field changes
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleStatusChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      status: event.target.checked,
    }));
  };

  const handleMobileChange = (event) => {
    const value = event.target.value;
    // Allow only numeric values and limit to 10 digits
    if (value === "" || (/^\d+$/.test(value) && value.length <= 10)) {
      setFormData((prev) => ({
        ...prev,
        mobile: value,
      }));

      // Clear error when field changes
      if (formErrors.mobile) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.mobile;
          return newErrors;
        });
      }
    }
  };

  const handleProfilePicChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      showNotification(
        "error",
        "Please upload a valid image file (JPEG, PNG, or GIF)"
      );
      return;
    }

    // Validate file size (1MB max)
    const maxSize = 1 * 1024 * 1024;
    if (file.size > maxSize) {
      showNotification("error", "Image file size must be less than 1MB");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      profilePic: file,
    }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Clear error
    if (formErrors.profilePic) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.profilePic;
        return newErrors;
      });
    }
  };

  const getFormattedData = (data) => {
    const formDataToSend = new FormData();

    formDataToSend.append("first_name", data.firstName);
    formDataToSend.append("last_name", data.lastName);
    formDataToSend.append("email", data.email);
    formDataToSend.append("employee_code", data.employeeCode || "");
    formDataToSend.append("mobile", data.mobile || "");
    formDataToSend.append("status", data.status ? 1 : 0);

    // Only add password if provided
    if (data.password) {
      formDataToSend.append("password", data.password);
    }

    // Add profile photo if it's a file
    if (data.profilePic instanceof File) {
      formDataToSend.append("profile_photo", data.profilePic);
    }

    return formDataToSend;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm(formData, mode);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstError = Object.values(errors)[0];
      showNotification("error", firstError);
      return;
    }

    try {
      setIsSubmitting(true);

      const submissionData = getFormattedData(formData);

      const endpoint =
        mode === "edit"
          ? `/delivery/drivers/update/${id}`
          : "/delivery/drivers/add";

      const res = await httpclient.post(endpoint, submissionData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const { data } = res;

      if (data.status === 200) {
        showNotification(
          "success",
          mode === "edit"
            ? "Driver successfully updated!"
            : "Driver successfully added!"
        );
        navigate("/delivery/drivers");
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/delivery/drivers");
  };

  if (loading) {
    return (
      <Container
        sx={{
          mt: 4,
          mb: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 2, mb: 2 }} maxWidth="lg">
      <Paper elevation={3} sx={{ overflow: "hidden", borderRadius: "12px" }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            {mode === "edit" ? "Edit Driver" : "Add New Driver"}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Profile Picture */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Avatar
                    src={profilePicPreview}
                    sx={{
                      width: 120,
                      height: 120,
                      border: "2px solid #E0E0E0",
                    }}
                  />
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    Upload Photo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleProfilePicChange}
                    />
                  </Button>
                  <Typography variant="caption" sx={{ textAlign: "center" }}>
                    Max size: 1MB (JPEG, PNG, GIF)
                  </Typography>
                </Box>
              </Grid>

              {/* First Name */}
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ fontSize: "14px", mb: 1 }}>
                  First Name <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <TextField
                  fullWidth
                  value={formData.firstName}
                  onChange={handleChange("firstName")}
                  error={!!formErrors.firstName}
                  helperText={formErrors.firstName}
                />
              </Grid>

              {/* Last Name */}
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ fontSize: "14px", mb: 1 }}>
                  Last Name <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <TextField
                  fullWidth
                  value={formData.lastName}
                  onChange={handleChange("lastName")}
                  error={!!formErrors.lastName}
                  helperText={formErrors.lastName}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ fontSize: "14px", mb: 1 }}>
                  Email <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <TextField
                  fullWidth
                  type="email"
                  value={formData.email}
                  onChange={handleChange("email")}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>

              {/* Mobile Number */}
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ fontSize: "14px", mb: 1 }}>
                  Mobile Number
                </InputLabel>
                <TextField
                  fullWidth
                  value={formData.mobile}
                  onChange={handleMobileChange}
                  error={!!formErrors.mobile}
                  helperText={
                    formErrors.mobile || "Enter 10 digit mobile number"
                  }
                  placeholder="Enter 10 digit mobile number"
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              </Grid>

              {/* Password */}
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ fontSize: "14px", mb: 1 }}>
                  Password{" "}
                  {mode === "add" && <span style={{ color: "red" }}>*</span>}
                </InputLabel>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange("password")}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  placeholder={
                    mode === "edit"
                      ? "Leave blank to keep current password"
                      : ""
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Confirm Password */}
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ fontSize: "14px", mb: 1 }}>
                  Confirm Password{" "}
                  {mode === "add" && <span style={{ color: "red" }}>*</span>}
                </InputLabel>
                <TextField
                  fullWidth
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange("confirmPassword")}
                  error={!!formErrors.confirmPassword}
                  helperText={formErrors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Employee Code */}
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ fontSize: "14px", mb: 1 }}>
                  Employee Code
                </InputLabel>
                <TextField
                  fullWidth
                  value={formData.employeeCode}
                  onChange={handleChange("employeeCode")}
                  error={!!formErrors.employeeCode}
                  helperText={formErrors.employeeCode}
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} md={6}>
                <InputLabel sx={{ fontSize: "14px", mb: 1 }}>Status</InputLabel>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.status}
                      onChange={handleStatusChange}
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 4,
                pt: 3,
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  backgroundColor: "#7152F3",
                  textTransform: "none",
                  position: "relative",
                  "&:hover": {
                    backgroundColor: "#5c34fa",
                  },
                }}
              >
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
                {mode === "edit" ? "Update Driver" : "Add Driver"}
              </Button>
            </Box>
          </form>
        </Box>
      </Paper>
    </Container>
  );
}

export default AddDriver;
